import { Directive, ElementRef, Input, OnChanges, SimpleChanges, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appBgCache]'
})
export class BgCacheDirective implements OnChanges {
  @Input('appBgCache') url: string | null | undefined;

  private currentTargetUrl: string = '';

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url'] && this.url) {
      const cleaned = this.cleanUrl(this.url);
      if (cleaned && cleaned !== this.currentTargetUrl) {
        this.currentTargetUrl = cleaned;
        this.loadAndCacheBackground(cleaned);
      }
    }
  }

  private cleanUrl(rawUrl: any): string {
    if (typeof rawUrl !== 'string') {
      return '';
    }
    const match = rawUrl.match(/url\(['"]?(.*?)['"]?\)/i);
    return match ? match[1] : rawUrl.trim();
  }

  private hashUrl(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString();
  }

  private loadAndCacheBackground(targetUrl: string): void {
    if (!targetUrl) return;

    const cacheKey = 'asap_bg_base64_' + this.hashUrl(targetUrl);
    const cachedBase64 = localStorage.getItem(cacheKey);

    // 1. INSTANT DISPLAY (0ms): If cached in localStorage, display 100% complete Base64 image immediately
    if (cachedBase64) {
      this.renderer.setStyle(this.el.nativeElement, 'backgroundImage', `url("${cachedBase64}")`);
      // Re-verify in background in case server image updated
      this.preloadInBackground(targetUrl, cacheKey, true);
      return;
    }

    // 2. FIRST VISIT / UNCACHED: Set a clean dark gradient fallback so page is styled instantly
    this.renderer.setStyle(
      this.el.nativeElement,
      'backgroundImage',
      'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)'
    );

    // 3. PRELOAD 100% IN MEMORY BEFORE DISPLAYING: Never assign backgroundImage while streaming bytes
    this.preloadInBackground(targetUrl, cacheKey, false);
  }

  private preloadInBackground(targetUrl: string, cacheKey: string, isAlreadyShowingCache: boolean): void {
    fetch(targetUrl)
      .then(res => {
        if (!res.ok) throw new Error('Fetch status ' + res.status);
        return res.blob();
      })
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result as string;
          if (base64Data && base64Data.length < 4500000) {
            this.safeSaveLocalStorage(cacheKey, base64Data);
          }
          this.applyBackground(base64Data, isAlreadyShowingCache);
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => {
        const img = new Image();
        img.onload = () => {
          this.applyBackground(targetUrl, isAlreadyShowingCache);
        };
        img.onerror = () => {
          this.applyBackground(targetUrl, isAlreadyShowingCache);
        };
        img.src = targetUrl;
      });
  }

  private safeSaveLocalStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      this.clearOldCaches();
      try {
        localStorage.setItem(key, value);
      } catch (err) {}
    }
  }

  private clearOldCaches(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith('asap_bg_') || k.startsWith('asap_img_'))) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch (e) {}
  }

  private applyBackground(imageSource: string, isAlreadyShowingCache: boolean): void {
    if (!imageSource) return;

    if (!isAlreadyShowingCache) {
      this.renderer.setStyle(this.el.nativeElement, 'transition', 'background-image 0.5s ease-in-out');
    }
    this.renderer.setStyle(this.el.nativeElement, 'backgroundImage', `url("${imageSource}")`);
  }
}

@Directive({
  selector: 'img[appImgCache]'
})
export class ImgCacheDirective implements OnChanges {
  @Input('appImgCache') url: string | null | undefined;

  private currentUrl = '';

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url'] && this.url) {
      const cleaned = this.cleanUrl(this.url);
      if (cleaned && cleaned !== this.currentUrl) {
        this.currentUrl = cleaned;
        this.loadAndCacheImage(cleaned);
      }
    }
  }

  private cleanUrl(rawUrl: any): string {
    if (typeof rawUrl !== 'string') return '';
    return rawUrl.trim();
  }

  private hashUrl(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString();
  }

  private loadAndCacheImage(targetUrl: string): void {
    if (!targetUrl) return;

    const cacheKey = 'asap_img_base64_' + this.hashUrl(targetUrl);
    const cachedBase64 = localStorage.getItem(cacheKey);

    // 1. INSTANT DISPLAY FROM CACHE (0ms):
    if (cachedBase64) {
      this.renderer.setAttribute(this.el.nativeElement, 'src', cachedBase64);
      this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
      // Re-verify in background in case server logo updated
      this.preloadInBackground(targetUrl, cacheKey);
      return;
    }

    // 2. UNCACHED: Hide image until 100% downloaded in memory to prevent line-by-line rendering
    this.renderer.setStyle(this.el.nativeElement, 'opacity', '0');
    this.renderer.setStyle(this.el.nativeElement, 'transition', 'opacity 0.25s ease-in-out');

    // 3. PRELOAD 100% IN MEMORY BEFORE DISPLAYING:
    this.preloadInBackground(targetUrl, cacheKey);
  }

  private preloadInBackground(targetUrl: string, cacheKey: string): void {
    fetch(targetUrl)
      .then(res => {
        if (!res.ok) throw new Error('Fetch status ' + res.status);
        return res.blob();
      })
      .then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result as string;
          if (base64Data && base64Data.length < 2500000) {
            try {
              localStorage.setItem(cacheKey, base64Data);
            } catch (e) {}
          }
          this.applyImage(base64Data);
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => {
        const img = new Image();
        img.onload = () => {
          this.applyImage(targetUrl);
        };
        img.onerror = () => {
          this.applyImage(targetUrl);
        };
        img.src = targetUrl;
      });
  }

  private applyImage(src: string): void {
    if (!src) return;
    this.renderer.setAttribute(this.el.nativeElement, 'src', src);
    this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
  }
}
